<template>
  <!--
    The order's sections — Timeline, Detail, Files, Activity Logs — and Ask
    ELSA, as icons at the right end of the action row.

    They were a row of labelled pills centred above the action row, which cost
    a full band of the pane's height for four words. As icons beside ⋯ they
    cost nothing: the row already exists. The name is in the tooltip and the
    accessible label; the active section is the filled one.

    ELSA is not a fifth section, she is someone you ask, so she is not in the
    group: her own button, purple against the group's teal, with her face and
    a sparkle. The freshness dot rides on her button.
  -->
  <nav class="osec" aria-label="Order sections">
    <div class="osec-group" role="tablist">
      <button
        v-for="tab in sections"
        :key="tab.id"
        type="button"
        role="tab"
        class="osec-btn"
        :class="{ 'osec-btn--on': tab.id === activeTab }"
        :aria-selected="tab.id === activeTab"
        :aria-label="tab.label"
        :title="tab.label"
        :data-test="`osec-${tab.id}`"
        @click="emit('select', tab.id)"
      >
        <component :is="ICONS[tab.id] ?? QueueListIcon" class="osec-icon" aria-hidden="true" />
      </button>
    </div>
    <!-- AB#21966 — Team chat: the order's internal chat, with its unread count. -->
    <button
      v-if="team"
      type="button"
      role="tab"
      class="osec-team"
      :class="{ 'osec-team--on': team.id === activeTab }"
      :aria-selected="team.id === activeTab"
      :aria-label="teamUnread ? `${team.label}, ${teamUnread} unread` : team.label"
      title="Team chat: internal, never shown to the customer or factory"
      data-test="osec-team_chat"
      @click="emit('select', team.id)"
    >
      <ChatBubbleLeftRightIcon class="osec-icon" aria-hidden="true" />
      <span v-if="teamUnread" class="osec-team-n" data-test="osec-team-unread">{{
        teamUnread > 99 ? '99+' : teamUnread
      }}</span>
    </button>
    <button
      v-if="elsa"
      type="button"
      role="tab"
      class="osec-elsa"
      :class="{ 'osec-elsa--on': elsa.id === activeTab }"
      :aria-selected="elsa.id === activeTab"
      :aria-label="elsa.label"
      :title="elsa.label"
      data-test="osec-ask_elsa"
      @click="emit('select', elsa.id)"
    >
      <span class="osec-elsa-face"><AgentAvatar agent="elsa" :size="22" /></span>
      <SparklesIcon class="osec-elsa-spark" aria-hidden="true" />
      <slot name="elsa-suffix"></slot>
    </button>
  </nav>
</template>

<script setup lang="ts">
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import {
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  DocumentDuplicateIcon,
  QueueListIcon,
  SparklesIcon,
} from '@heroicons/vue/24/outline';
import { computed, h, type Component } from 'vue';

interface SectionTab {
  id: string;
  label: string;
  variant?: 'agent' | 'team';
}

const props = withDefaults(
  defineProps<{
    tabs: SectionTab[];
    activeTab: string;
    /** AB#21966 — unread team chat messages on this order. */
    teamUnread?: number;
  }>(),
  { teamUnread: 0 }
);

const emit = defineEmits<{ (e: 'select', id: string): void }>();

/** The jersey: Heroicons has no garment, and Detail is where the products are. */
const ShirtIcon: Component = () =>
  h(
    'svg',
    {
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.6,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    },
    [
      h('path', {
        d: 'M8 4.5 5 6.2 3 10l2.5 1.2V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-7.8L21 10l-2-3.8-3-1.7a3.2 3.2 0 0 1-8 0Z',
      }),
    ]
  );

const ICONS: Record<string, Component> = {
  timeline: QueueListIcon,
  detail: ShirtIcon,
  files: DocumentDuplicateIcon,
  activity_logs: ClipboardDocumentCheckIcon,
};

const sections = computed(() => props.tabs.filter((tab) => !tab.variant));
const team = computed(() => props.tabs.find((tab) => tab.variant === 'team') ?? null);
const elsa = computed(() => props.tabs.find((tab) => tab.variant === 'agent') ?? null);
</script>

<style lang="scss">
/* px on purpose: the root font size is 12.8px. Unscoped: the nav is rendered
   through a slot inside the action row, and these classes are its own. */
.osec {
  @apply flex items-center gap-[6px] shrink-0;
}
.osec-group {
  @apply inline-flex items-center gap-[2px] p-[2px] bg-surface border border-line rounded-[7px];
}
.osec-btn {
  @apply flex items-center justify-center w-[32px] h-[28px] rounded-[5px] text-ink-soft
         bg-transparent border-0 cursor-pointer transition-colors;
}
.osec-btn:hover {
  @apply bg-[#eef0f3] text-ink;
}
.osec-btn--on,
.osec-btn--on:hover {
  @apply bg-[var(--accent)] text-white;
}
.osec-btn:focus-visible,
.osec-elsa:focus-visible {
  @apply outline outline-2 outline-offset-1 outline-[var(--accent)];
}
.osec-icon {
  width: 19px;
  height: 19px;
}
/* AB#21966 — Team chat: its own button between the sections and ELSA. */
.osec-team {
  @apply relative flex items-center justify-center w-[36px] h-[34px] rounded-[8px] cursor-pointer
         border border-line bg-surface text-ink-soft transition-colors;
}
.osec-team:hover {
  @apply text-ink border-[var(--accent)];
}
.osec-team--on,
.osec-team--on:hover {
  @apply border-[var(--accent)] text-[var(--accent)] bg-[var(--accent-soft)];
  box-shadow: 0 0 0 2px rgba(79, 124, 240, 0.25);
}
.osec-team:focus-visible {
  @apply outline outline-2 outline-offset-1 outline-[var(--accent)];
}
.osec-team-n {
  @apply absolute flex items-center justify-center min-w-[17px] h-[17px] px-[4px] rounded-full
         text-[10px] font-bold text-white bg-[var(--accent)];
  top: -7px;
  right: -7px;
  box-shadow: 0 0 0 2px var(--surface);
}
.osec-elsa {
  @apply relative flex items-center justify-center w-[36px] h-[34px] rounded-[8px] cursor-pointer
         border border-[#d6cdfb] text-[#5b51c4] transition-colors;
  background: linear-gradient(135deg, #efeaff, #e4f0ff);
}
.osec-elsa:hover {
  background: linear-gradient(135deg, #e6ddff, #d9eaff);
}
.osec-elsa--on {
  @apply border-[#7c5cd6];
  background: linear-gradient(135deg, #e3d9ff, #d3e6ff);
  box-shadow: 0 0 0 2px rgba(124, 92, 214, 0.25);
}
.osec-elsa-face {
  @apply block w-[22px] h-[22px];
}
.osec-elsa-spark {
  @apply absolute w-[12px] h-[12px] text-[#7c5cd6];
  top: 3px;
  right: 3px;
}
/* The freshness dot, bottom-right so it never collides with the sparkle. */
.osec-elsa-dot {
  @apply absolute block w-[8px] h-[8px] rounded-full bg-[#ce7415] border-2 border-white;
  bottom: -3px;
  right: -3px;
}
</style>
